import * as XLSX from 'xlsx';

import type { AutorNaturezaItem, NaturezaAcaoOption } from '@/modules/reports/presentation/pages/relatorio-civel-page.models';

export type LinhaPreviewImportAutorNatureza = {
  /** Número da linha na planilha (1 = cabeçalho). */
  indicePlanilha: number;
  autor: string;
  naturezaTexto: string;
  naturezaId: string | null;
  naturezaLabel: string | null;
  valido: boolean;
  erros: string[];
};

function normalizarCabecalho(c: string): string {
  return c
    .replace(/\u00A0/g, ' ')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function encontrarChavesAutorNatureza(primeiraLinha: Record<string, unknown>): { autorKey: string; naturezaKey: string } | null {
  const keysOrig = Object.keys(primeiraLinha);
  if (keysOrig.length === 0) return null;

  const pares = keysOrig.map((orig) => ({ orig, norm: normalizarCabecalho(orig) }));

  let autorKey = pares.find((p) => p.norm === 'autor')?.orig;
  if (!autorKey) autorKey = pares.find((p) => p.norm.includes('autor') && !p.norm.includes('natureza'))?.orig;

  const naturezaKey = pares.find((p) => p.orig !== autorKey && p.norm.includes('natureza'))?.orig;

  if (!autorKey || !naturezaKey || autorKey === naturezaKey) return null;
  return { autorKey, naturezaKey };
}

export function resolverNaturezaAcaoId(
  texto: string,
  naturezaAcaoOptions: Pick<NaturezaAcaoOption, 'value' | 'label'>[],
): { id: string | null; label: string | null } {
  const t = texto.trim();
  if (!t) return { id: null, label: null };

  const byValue = naturezaAcaoOptions.find((o) => o.value === t);
  if (byValue) return { id: byValue.value, label: byValue.label };

  const low = t.toLowerCase();
  const byLabelEq = naturezaAcaoOptions.find((o) => o.label.trim().toLowerCase() === low);
  if (byLabelEq) return { id: byLabelEq.value, label: byLabelEq.label };

  const byLabelContains = naturezaAcaoOptions.find(
    (o) =>
      o.label.trim().toLowerCase().includes(low) ||
      low.includes(o.label.trim().toLowerCase()),
  );
  if (byLabelContains) return { id: byLabelContains.value, label: byLabelContains.label };

  return { id: null, label: null };
}

/**
 * Lê a primeira aba de um Excel (.xlsx/.xls) com colunas de cabeçalho Autor e Natureza da ação.
 */
export function parseAutorNaturezaExcel(
  arrayBuffer: ArrayBuffer,
  naturezaAcaoOptions: Pick<NaturezaAcaoOption, 'value' | 'label'>[],
): { linhas: LinhaPreviewImportAutorNatureza[]; erroCabeçalho: string | null } {
  const wb = XLSX.read(arrayBuffer, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  const sheet = wb.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '', raw: false });

  if (rows.length === 0) {
    return { linhas: [], erroCabeçalho: 'A planilha está vazia.' };
  }

  const chaves = encontrarChavesAutorNatureza(rows[0]);
  if (!chaves) {
    return {
      linhas: [],
      erroCabeçalho:
        'Não foi possível identificar as colunas. Use os cabeçalhos "Autor" e "Natureza da ação" na primeira linha.',
    };
  }

  const { autorKey, naturezaKey } = chaves;
  const linhas: LinhaPreviewImportAutorNatureza[] = [];

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const autor = String(row[autorKey] ?? '').trim();
    const naturezaTexto = String(row[naturezaKey] ?? '').trim();

    if (!autor && !naturezaTexto) {
      continue;
    }

    const erros: string[] = [];
    if (!autor) erros.push('Autor em branco.');
    if (!naturezaTexto) erros.push('Natureza da ação em branco.');

    const { id, label } = resolverNaturezaAcaoId(naturezaTexto, naturezaAcaoOptions);
    if (naturezaTexto && !id) {
      erros.push('Natureza não encontrada entre as opções do sistema (use o mesmo texto do cadastro ou o código).');
    }

    const valido = erros.length === 0;
    linhas.push({
      indicePlanilha: i + 2,
      autor,
      naturezaTexto,
      naturezaId: id,
      naturezaLabel: label,
      valido,
      erros,
    });
  }

  return { linhas, erroCabeçalho: null };
}

export function montarItensAutorNaturezaImportados(
  linhas: LinhaPreviewImportAutorNatureza[],
): AutorNaturezaItem[] {
  const agora = new Date();
  return linhas
    .filter((l) => l.valido && l.naturezaId)
    .map((l, i) => ({
      id: `import-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 9)}`,
      autor: l.autor,
      naturezaAcao: l.naturezaId!,
      dataAdicao: agora,
    }));
}
